import React from 'react';
import { Box, Button, Card, Chip, Typography } from '@mui/material';
import UnsplashAttribution from './UnsplashAttribution';
import TripBookedBy from './TripBookedBy';
import {
  DOCUMENT,
  documentEyebrowSx,
  documentSolidButtonSmallSx,
} from '../constants/documentTheme';

const coverBadgeSx = {
  fontWeight: 500,
  fontSize: 9.5,
  letterSpacing: '0.16em',
  textTransform: 'uppercase',
  height: 22,
  borderRadius: 0,
  backgroundColor: '#C9A961',
  color: '#2A070C',
  border: 0,
  '& .MuiChip-label': { px: 1.1 },
};

const DocumentPackageCard = ({
  testId,
  imageUrl,
  imageAlt,
  containImage = false,
  photographerName,
  photographerUsername,
  badges = [],
  offeredBy,
  onOfferedByClick,
  coverLabel,
  eyebrow,
  title,
  location,
  description,
  rows = [],
  actionLabel,
  actionHref,
  onClick,
  onAction,
}) => {
  const showCredit = photographerName || (imageUrl && String(imageUrl).includes('images.unsplash.com'));

  return (
    <Card
      data-testid={testId}
      onClick={(event) => {
        if (actionHref) {
          window.open(actionHref, '_blank', 'noopener,noreferrer');
          return;
        }
        onClick?.(event);
      }}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: DOCUMENT.surface,
        border: `1px solid ${DOCUMENT.ruleHard}`,
        borderRadius: 0,
        boxShadow: 'none',
        overflow: 'hidden',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'border-color 0.2s',
        '&:hover': {
          borderColor: DOCUMENT.accent,
        },
      }}
    >
      <Box sx={{ position: 'relative', aspectRatio: '16 / 9', bgcolor: DOCUMENT.oxblood, overflow: 'hidden' }}>
        <Box
          component="img"
          src={imageUrl}
          alt={imageAlt || title}
          onError={(event) => {
            const fallback = 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=1200&q=80';
            if (event.currentTarget.src !== fallback) {
              event.currentTarget.src = fallback;
              return;
            }
            event.currentTarget.style.display = 'none';
          }}
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: containImage ? 'contain' : 'cover',
            objectPosition: 'center',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to top, rgba(18,5,8,0.78) 0%, rgba(18,5,8,0.2) 46%, transparent 74%)',
            pointerEvents: 'none',
          }}
        />
        {(badges.length > 0 || offeredBy?.name) && (
          <Box sx={{ position: 'absolute', top: 13, left: 15, right: 15, zIndex: 3, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {badges.filter(Boolean).map((badge) => (
              <Chip key={badge} label={badge} size="small" sx={coverBadgeSx} />
            ))}
            {offeredBy?.name ? (
              <TripBookedBy
                variant="cover"
                labelKey="trip.offeredByLabel"
                name={offeredBy.name}
                logoUrl={offeredBy.logoUrl}
                banner={offeredBy.banner}
                onClick={onOfferedByClick ? (event) => {
                  event.stopPropagation();
                  onOfferedByClick(event);
                } : undefined}
              />
            ) : null}
          </Box>
        )}
        {coverLabel && (
          <Typography
            sx={{
              position: 'absolute',
              left: 15,
              bottom: 12,
              zIndex: 3,
              fontSize: 10,
              letterSpacing: '0.19em',
              textTransform: 'uppercase',
              color: DOCUMENT.ivory,
            }}
          >
            {coverLabel}
          </Typography>
        )}
      </Box>

      {showCredit && (
        <UnsplashAttribution
          imageUrl={imageUrl}
          photographerName={photographerName}
          photographerUsername={photographerUsername}
          variant="below"
          sx={{ mt: 0, px: 2.25, pt: 0.85, pb: 0, '& .MuiTypography-root': { fontSize: 10, color: DOCUMENT.ink3 } }}
        />
      )}

      <Box sx={{ px: 2.25, pt: 1.5, pb: 0, flex: 1 }}>
        {eyebrow && <Typography sx={documentEyebrowSx}>{eyebrow}</Typography>}
        <Typography
          component="h3"
          sx={{
            fontFamily: DOCUMENT.display,
            fontWeight: 400,
            fontSize: 23,
            lineHeight: 1.14,
            mt: 0.75,
            mb: 0.5,
            color: DOCUMENT.ink,
          }}
        >
          {title}
        </Typography>
        {location && (
          <Typography sx={{ fontSize: 13, color: DOCUMENT.ink2 }}>
            {location}
          </Typography>
        )}
        {description && (
          <Typography
            sx={{
              mt: 1.1,
              fontSize: 13,
              color: DOCUMENT.ink3,
              lineHeight: 1.6,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {description}
          </Typography>
        )}

        {rows.length > 0 && (
          <Box component="dl" sx={{ mt: 1.75, mb: 0, borderTop: `1px solid ${DOCUMENT.rule}` }}>
            {rows.map((row) => (
              <Box
                key={row.label}
                component="div"
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 1.75,
                  py: 1.1,
                  borderBottom: `1px solid ${DOCUMENT.rule}`,
                }}
              >
                <Typography component="dt" sx={{ fontSize: 12.5, color: DOCUMENT.ink3, m: 0 }}>
                  {row.label}
                </Typography>
                <Typography
                  component="dd"
                  sx={{
                    fontSize: 13.5,
                    fontVariantNumeric: 'tabular-nums',
                    textAlign: 'right',
                    m: 0,
                    color: DOCUMENT.ink,
                  }}
                >
                  {row.value}
                  {row.extra && (
                    <Box component="em" sx={{ fontStyle: 'normal', color: DOCUMENT.ink3, fontSize: 12, ml: 0.6 }}>
                      {row.extra}
                    </Box>
                  )}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {actionLabel && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 2.25,
            py: 1.75,
            mt: 1.75,
            borderTop: `1px solid ${DOCUMENT.rule}`,
          }}
        >
          <Button
            component={actionHref ? 'a' : 'button'}
            href={actionHref || undefined}
            target={actionHref ? '_blank' : undefined}
            rel={actionHref ? 'noopener noreferrer' : undefined}
            onClick={(event) => {
              event.stopPropagation();
              if (actionHref) return;
              (onAction || onClick)?.(event);
            }}
            sx={{ ...documentSolidButtonSmallSx, ml: 'auto' }}
          >
            {actionLabel}
          </Button>
        </Box>
      )}
    </Card>
  );
};

export default DocumentPackageCard;
